import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import SearchableSelect from '../components/ui/SearchableSelect';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import Modal from '../components/ui/Modal';
import EmailTemplateManager from '../components/email/EmailTemplateManager';
import Badge from '../components/ui/Badge';
import { useSettings } from '../store/SettingsContext';
import { useUser } from '../store/UserContext';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import SmtpSettingsService from '../services/SmtpSettingsService';
import EmailService from '../services/EmailService';
import DatabaseSettingsService from '../services/DatabaseSettingsService';
import LicenseService from '../services/LicenseService';
import FeaturePackService from '../services/FeaturePackService';
import RoleSettingsService from '../services/RoleSettingsService';
import { formatDateTime } from '../utils/dateFormat';
import { PACK_DEFINITIONS } from '../config/featurePacks';
import {
  getAdminPortalLoginUrl,
  getStaffPortalLoginUrl,
  getStudentPortalLoginUrl
} from '../utils/loginLinks';

export default function SettingsPage() {
  const { settings, setSettings } = useSettings();
  const { user, setUser } = useUser();
  const location = useLocation();
  const staffService = useMemo(() => new StaffService(), []);
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const smtpService = useMemo(() => new SmtpSettingsService(), []);
  const emailService = useMemo(() => new EmailService(), []);
  const dbService = useMemo(() => new DatabaseSettingsService(), []);
  const licenseService = useMemo(() => new LicenseService(), []);
  const packService = useMemo(() => new FeaturePackService(), []);
  const roleService = useMemo(() => new RoleSettingsService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);
  const roleModules = useMemo(() => roleService.getModules(), [roleService]);
  const loginOptions = useMemo(
    () =>
      staffList
        .filter((staff) => staff.loginEnabled)
        .map((staff) => ({
          value: staff.id,
          label: staff.name,
          meta: staff.accessScope ? staff.accessScope.toUpperCase() : 'STAFF'
        })),
    [staffList]
  );

  const [activeTab, setActiveTab] = useState('general');
  const [studentPortalTab, setStudentPortalTab] = useState('motivation');
  const [draft, setDraft] = useState(settings);
  const [logoPreview, setLogoPreview] = useState(settings.siteLogo || '');
  const [savedMessage, setSavedMessage] = useState('');
  const [smtpDraft, setSmtpDraft] = useState(() => smtpService.get());
  const [smtpMessage, setSmtpMessage] = useState('');
  const [smtpTestEmail, setSmtpTestEmail] = useState(user?.email || '');
  const [dbDraft, setDbDraft] = useState(() => dbService.get());
  const [dbMessage, setDbMessage] = useState('');
  const [licenseDraft, setLicenseDraft] = useState(() => licenseService.get());
  const [licenseMessage, setLicenseMessage] = useState('');
  const [packDraft, setPackDraft] = useState(() => packService.get());
  const [packSnapshot, setPackSnapshot] = useState(() => packService.get().packs || {});
  const [packMessage, setPackMessage] = useState('');
  const [packStatus, setPackStatus] = useState('');
  const [serialKey, setSerialKey] = useState(licenseService.get().serialKey || '');
  const [roles, setRoles] = useState(() => roleService.list());
  const [activeRoleId, setActiveRoleId] = useState(() => roleService.list()[0]?.id || 'admin');
  const [roleMessage, setRoleMessage] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [wellbeingModalOpen, setWellbeingModalOpen] = useState(false);
  const [infoStatus, setInfoStatus] = useState('');
  const isAdmin = user?.accessScope === 'admin';
  const adminPortalLoginUrl = getAdminPortalLoginUrl();
  const staffPortalLoginUrl = getStaffPortalLoginUrl();
  const studentPortalLoginUrl = getStudentPortalLoginUrl();

  useEffect(() => {
    setDraft(settings);
    setLogoPreview(settings.siteLogo || '');
  }, [settings]);

  useEffect(() => {
    setSmtpDraft(smtpService.get());
  }, [smtpService]);

  useEffect(() => {
    setDbDraft(dbService.get());
  }, [dbService]);

  useEffect(() => {
    setLicenseDraft(licenseService.get());
  }, [licenseService]);

  useEffect(() => {
    setSerialKey(licenseDraft.serialKey || '');
  }, [licenseDraft.serialKey]);

  useEffect(() => {
    const current = packService.get();
    setPackDraft(current);
    setPackSnapshot(current.packs || {});
  }, [packService]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    setRoles(roleService.list());
  }, [roleService]);

  useEffect(() => {
    if (roles.find((role) => role.id === activeRoleId)) return;
    setActiveRoleId(roles[0]?.id || 'admin');
  }, [roles, activeRoleId]);

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result?.toString() || '';
      setLogoPreview(dataUrl);
      setDraft((prev) => ({ ...prev, siteLogo: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const handleLoginSwitch = (staffId) => {
    if (!staffId) return;
    const staff = staffList.find((member) => member.id === staffId);
    if (!staff) return;
    const programmeNames = (staff.programmes || staff.programmeIds || []).map((programmeId) => {
      const programme = programmes.find((entry) => entry.id === programmeId || entry.name === programmeId);
      return programme?.name || programmeId;
    });
    setUser({
      id: staff.id,
      name: staff.name,
      role: staff.role || 'Support Worker',
      title: staff.title || staff.role || 'Support Worker',
      accessScope: staff.accessScope || 'staff',
      roleKey: staff.roleKey || (staff.accessScope === 'admin' ? 'admin' : staff.accessScope === 'inventory' ? 'inventory' : 'teacher'),
      assignedProgrammes: programmeNames
    });
  };

  const resetToAdmin = () => {
    setUser({
      id: 'user-1',
      name: 'Admin User',
      role: 'Manager',
      title: 'Centre Manager',
      accessScope: 'admin',
      roleKey: 'admin'
    });
  };

  const handleSave = () => {
    setSettings(draft);
    setSavedMessage('Settings saved');
    window.setTimeout(() => setSavedMessage(''), 2000);
  };

  const handleSmtpSave = () => {
    smtpService.save(smtpDraft, user);
    setSmtpMessage('SMTP settings saved.');
    window.setTimeout(() => setSmtpMessage(''), 2000);
  };

  const handleSmtpTest = () => {
    if (!smtpTestEmail.trim()) {
      setSmtpMessage('Enter a test email address.');
      return;
    }
    const log = emailService.sendRaw({
      to: smtpTestEmail.trim(),
      subject: 'SafeguardPro SMTP test',
      html: '<p>This is a SafeguardPro SMTP test email.</p>',
      text: 'This is a SafeguardPro SMTP test email.',
      templateKey: 'smtp.test',
      entity: { type: 'smtp', id: 'test' }
    });
    if (log.status === 'failed') {
      setSmtpMessage(`Test failed: ${log.error_message}`);
    } else {
      setSmtpMessage('Test email sent.');
    }
  };

  const handleDbSave = () => {
    dbService.save(dbDraft, user);
    setDbMessage('Database settings saved.');
    window.setTimeout(() => setDbMessage(''), 2000);
  };

  const handleLicenseSave = () => {
    const next = licenseService.save({ ...licenseDraft, serialKey }, user);
    setLicenseDraft(next);
    setLicenseMessage('Serial key saved.');
    window.setTimeout(() => setLicenseMessage(''), 2000);
  };

  const handlePackToggle = (packId, nextValue) => {
    setPackDraft((prev) => {
      const nextPacks = { ...(prev.packs || {}), [packId]: nextValue };
      if (packId === 'student_portal' && !nextValue) {
        nextPacks.rewards_points = false;
      }
      return { ...prev, packs: nextPacks };
    });
  };

  const handlePackSave = () => {
    const result = packService.update(packDraft.packs, user);
    setPackDraft(result);
    setPackSnapshot(result.packs || {});
    setPackMessage('Pack settings saved.');
    window.setTimeout(() => setPackMessage(''), 2000);
  };

  const handleSerialValidate = () => {
    const result = packService.validateSerialKey(serialKey);
    setPackStatus(result.message || 'Validation not implemented.');
  };

  const packChangeCount = useMemo(() => {
    return PACK_DEFINITIONS.reduce((count, pack) => {
      const before = Boolean(packSnapshot?.[pack.id]);
      const after = Boolean(packDraft.packs?.[pack.id]);
      return before === after ? count : count + 1;
    }, 0);
  }, [packSnapshot, packDraft.packs]);

  const hasPackChanges = packChangeCount > 0;

  const activeRole = roles.find((role) => role.id === activeRoleId) || roles[0];
  const roleAssignments = useMemo(() => {
    return staffList.reduce((acc, staff) => {
      const key = staff.roleKey || (staff.accessScope === 'admin' ? 'admin' : staff.accessScope === 'inventory' ? 'inventory' : 'teacher');
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [staffList]);

  const handleRoleUpdate = (updates) => {
    if (!activeRole) return;
    const updated = roleService.update({ ...activeRole, ...updates });
    setRoles(roleService.list());
    setActiveRoleId(updated?.id || activeRoleId);
    window.dispatchEvent(new Event('roles:updated'));
  };

  const handleCreateRole = () => {
    const name = newRoleName.trim();
    if (!name) {
      setRoleMessage('Enter a role name.');
      return;
    }
    const created = roleService.add({ name, description: newRoleDescription.trim() });
    setRoles(roleService.list());
    setActiveRoleId(created.id);
    setNewRoleName('');
    setNewRoleDescription('');
    setRoleMessage('Role created.');
    window.setTimeout(() => setRoleMessage(''), 2000);
    window.dispatchEvent(new Event('roles:updated'));
  };

  const handleDeleteRole = () => {
    if (!activeRole) return;
    if (activeRole.system) {
      setRoleMessage('System roles cannot be deleted.');
      return;
    }
    const assignedCount = roleAssignments[activeRole.id] || 0;
    if (assignedCount > 0) {
      setRoleMessage('Reassign staff before deleting this role.');
      return;
    }
    const ok = window.confirm(`Delete role "${activeRole.name}"? This cannot be undone.`);
    if (!ok) return;
    roleService.remove(activeRole.id);
    const nextRoles = roleService.list();
    setRoles(nextRoles);
    setActiveRoleId(nextRoles[0]?.id || 'admin');
    setRoleMessage('Role deleted.');
    window.setTimeout(() => setRoleMessage(''), 2000);
    window.dispatchEvent(new Event('roles:updated'));
  };

  const handleReset = () => {
    setDraft(settings);
    setLogoPreview(settings.siteLogo || '');
  };

  const handleCopyLink = async (value, label) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        setInfoStatus(`${label} link copied.`);
      } else {
        setInfoStatus(`${label} link: ${value}`);
      }
    } catch (error) {
      setInfoStatus(`Could not copy automatically. ${label} link: ${value}`);
    }
    window.setTimeout(() => setInfoStatus(''), 2400);
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'info', label: 'Info & links' },
    { id: 'student-portal', label: 'Student portal' },
    { id: 'roles', label: 'Roles & access' },
    { id: 'database', label: 'MySQL' },
    { id: 'smtp', label: 'SMTP' },
    { id: 'templates', label: 'Email templates' },
    { id: 'licensing', label: 'Licensing & Packs' }
  ];

  return (
    <div className="space-y-6">
      <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Settings</h2>
      <Tabs
        tabs={tabs}
        active={activeTab}
        onChange={setActiveTab}
        ariaLabel="Settings sections"
        idBase="settings"
      />

      {activeTab === 'general' && (
        <section id="settings-panel-general" role="tabpanel" aria-labelledby="settings-tab-general" className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            {savedMessage && <span className="text-xs sp-muted">{savedMessage}</span>}
            <Button size="sm" variant="secondary" type="button" onClick={handleReset} disabled={!isDirty}>
              Reset
            </Button>
            <Button size="sm" type="button" onClick={handleSave} disabled={!isDirty}>
              Save changes
            </Button>
          </div>

          <Card className="p-6 space-y-4">
            <div>
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Site identity</h3>
              <p className="sp-muted">Update branding shown across the platform.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sp-label">Site name</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={draft.siteName}
                  onChange={(event) => setDraft((prev) => ({ ...prev, siteName: event.target.value }))}
                  placeholder="Centre name"
                />
              </div>
              <div>
                <label className="sp-label">Site subtitle</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={draft.siteSubtitle}
                  onChange={(event) => setDraft((prev) => ({ ...prev, siteSubtitle: event.target.value }))}
                  placeholder="Tagline"
                />
              </div>
              <div>
                <label className="sp-label">Site logo</label>
                <div className="flex items-center gap-3 mt-2">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Site logo" className="h-10 w-10 rounded" />
                  ) : (
                    <div className="h-10 w-10 rounded bg-slate-200 text-xs flex items-center justify-center">
                      No logo
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleLogoChange} />
                  {logoPreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setLogoPreview('');
                        setDraft((prev) => ({ ...prev, siteLogo: '' }));
                      }}
                      type="button"
                    >
                      Remove logo
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <div>
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Date & attendance defaults</h3>
              <p className="sp-muted">These settings apply across the workspace views and reports.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="sp-label">Date format</label>
                <select
                  className="sp-select mt-2 w-full"
                  value={draft.dateFormat}
                  onChange={(event) => setDraft((prev) => ({ ...prev, dateFormat: event.target.value }))}
                >
                  <option value="UK">UK (DD/MM/YYYY)</option>
                  <option value="US">US (MM/DD/YYYY)</option>
                  <option value="ISO">ISO (YYYY-MM-DD)</option>
                </select>
              </div>
              <div>
                <label className="sp-label">Attendance threshold (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="sp-input mt-2 w-full"
                  value={draft.attendanceThreshold}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, attendanceThreshold: Number(event.target.value) || 0 }))
                  }
                />
              </div>
              <div>
                <label className="sp-label">Default report range</label>
                <select
                  className="sp-select mt-2 w-full"
                  value={draft.defaultReportRange}
                  onChange={(event) => setDraft((prev) => ({ ...prev, defaultReportRange: event.target.value }))}
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="term">Term (90 days)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <div>
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Access & session</h3>
              <p className="sp-muted">Switch between staff accounts to validate role-based visibility.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sp-label">Current session</label>
                <div className="sp-panel sp-panel--muted p-3 mt-2 text-sm">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{user.name}</div>
                  <div className="text-xs sp-muted">{user.role} · {user.accessScope?.toUpperCase() || 'ADMIN'}</div>
                </div>
              </div>
              <div>
                <label className="sp-label">Login as</label>
                <SearchableSelect
                  value=""
                  options={loginOptions}
                  placeholder="Search staff with login access"
                  emptyLabel="No staff logins enabled"
                  ariaLabel="Select staff login"
                  className="mt-2"
                  onChange={handleLoginSwitch}
                />
                <div className="text-xs sp-muted mt-2">Only staff with login access enabled are listed.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" type="button" onClick={resetToAdmin}>
                Return to admin
              </Button>
            </div>
          </Card>
        </section>
      )}

      {activeTab === 'info' && (
        <section id="settings-panel-info" role="tabpanel" aria-labelledby="settings-tab-info" className="space-y-4">
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Login links</h3>
              <p className="sp-muted">Share these links with staff and students when onboarding.</p>
            </div>
            <div className="space-y-3">
              <div className="sp-panel sp-panel--muted p-3">
                <div className="sp-label">Admin panel login</div>
                <div className="text-sm mt-1 break-all">{adminPortalLoginUrl}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Button size="sm" type="button" variant="secondary" onClick={() => handleCopyLink(adminPortalLoginUrl, 'Admin')}>
                    Copy link
                  </Button>
                  <a className="sp-link text-sm" href={adminPortalLoginUrl} target="_blank" rel="noopener noreferrer">
                    Open link
                  </a>
                </div>
              </div>
              <div className="sp-panel sp-panel--muted p-3">
                <div className="sp-label">Staff portal login</div>
                <div className="text-sm mt-1 break-all">{staffPortalLoginUrl}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Button size="sm" type="button" variant="secondary" onClick={() => handleCopyLink(staffPortalLoginUrl, 'Staff')}>
                    Copy link
                  </Button>
                  <a className="sp-link text-sm" href={staffPortalLoginUrl} target="_blank" rel="noopener noreferrer">
                    Open link
                  </a>
                </div>
              </div>
              <div className="sp-panel sp-panel--muted p-3">
                <div className="sp-label">Student portal login</div>
                <div className="text-sm mt-1 break-all">{studentPortalLoginUrl}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Button size="sm" type="button" variant="secondary" onClick={() => handleCopyLink(studentPortalLoginUrl, 'Student portal')}>
                    Copy link
                  </Button>
                  <a className="sp-link text-sm" href={studentPortalLoginUrl} target="_blank" rel="noopener noreferrer">
                    Open link
                  </a>
                </div>
              </div>
            </div>
            {infoStatus && (
              <div className="text-xs sp-muted" role="status" aria-live="polite">
                {infoStatus}
              </div>
            )}
          </Card>
        </section>
      )}

      {activeTab === 'student-portal' && (
        <section id="settings-panel-student-portal" role="tabpanel" aria-labelledby="settings-tab-student-portal">
          {!isAdmin ? (
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Student portal</h3>
            <p className="sp-muted">Student portal settings are restricted to admin users.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant={studentPortalTab === 'motivation' ? 'secondary' : 'ghost'}
                type="button"
                onClick={() => setStudentPortalTab('motivation')}
              >
                Motivation
              </Button>
              <Button
                size="sm"
                variant={studentPortalTab === 'points' ? 'secondary' : 'ghost'}
                type="button"
                onClick={() => setStudentPortalTab('points')}
              >
                Points & rewards
              </Button>
              <Button
                size="sm"
                variant={studentPortalTab === 'extras' ? 'secondary' : 'ghost'}
                type="button"
                onClick={() => setStudentPortalTab('extras')}
              >
                Extras
              </Button>
              <Button
                size="sm"
                variant={studentPortalTab === 'banner' ? 'secondary' : 'ghost'}
                type="button"
                onClick={() => setStudentPortalTab('banner')}
              >
                Banner
              </Button>
            </div>
            {studentPortalTab === 'motivation' && (
              <Card className="p-6 space-y-4">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Motivation & content</h3>
                <p className="sp-muted">Configure the positive quote API, featured quotes, and optional custom HTML block.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="sp-label">Quote source</label>
                  <select
                    className="sp-select mt-2 w-full"
                    value={draft.studentPortal?.quoteMode || (draft.studentPortal?.quoteApiEnabled ? 'fallback' : 'off')}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: {
                          ...prev.studentPortal,
                          quoteMode: event.target.value,
                          quoteApiEnabled: event.target.value !== 'off'
                        }
                      }))
                    }
                  >
                    <option value="off">Turn off motivation block</option>
                    <option value="featured">Use featured quotes only</option>
                    <option value="api">Use API only</option>
                    <option value="fallback">Use API with fallback quotes</option>
                  </select>
                  <div className="text-xs sp-muted mt-2">
                    Featured quotes are managed below. Fallback mode shows a featured quote if the API fails.
                  </div>
                </div>
                <div>
                  <label className="sp-label">Quote API URL</label>
                  <input
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.quoteApiUrl || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, quoteApiUrl: event.target.value }
                      }))
                    }
                    placeholder="https://api.quotable.io/quotes/random?tags=motivational|inspirational&limit=1"
                  />
                  <div className="text-xs sp-muted mt-1">
                    Provide the full API URL (supports HTTPS). Example uses Quotable.
                  </div>
                </div>
                <div>
                  <label className="sp-label">Quote field</label>
                  <input
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.quoteField || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, quoteField: event.target.value }
                      }))
                    }
                    placeholder="q or content"
                  />
                </div>
                <div>
                  <label className="sp-label">Author field</label>
                  <input
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.authorField || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, authorField: event.target.value }
                      }))
                    }
                    placeholder="author"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label">API headers (JSON)</label>
                  <textarea
                    className="sp-textarea mt-2 w-full"
                    rows={3}
                    value={draft.studentPortal?.quoteApiHeaders || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, quoteApiHeaders: event.target.value }
                      }))
                    }
                    placeholder='{"Authorization":"Bearer ..."}'
                  />
                  <div className="text-xs sp-muted mt-1">Leave blank if no headers are required.</div>
                </div>
                <div className="md:col-span-2 space-y-3">
                  <div className="sp-label">Featured quotes</div>
                  {(draft.studentPortal?.localQuotes || []).map((quote, index) => (
                    <div key={quote.id || index} className="grid md:grid-cols-[1fr_220px_auto] gap-3">
                      <input
                        className="sp-input w-full"
                        value={quote.text || ''}
                        onChange={(event) => {
                          const nextQuotes = [...(draft.studentPortal?.localQuotes || [])];
                          nextQuotes[index] = { ...quote, text: event.target.value };
                          setDraft((prev) => ({
                            ...prev,
                            studentPortal: { ...prev.studentPortal, localQuotes: nextQuotes }
                          }));
                        }}
                        placeholder="Quote text"
                      />
                      <input
                        className="sp-input w-full"
                        value={quote.author || ''}
                        onChange={(event) => {
                          const nextQuotes = [...(draft.studentPortal?.localQuotes || [])];
                          nextQuotes[index] = { ...quote, author: event.target.value };
                          setDraft((prev) => ({
                            ...prev,
                            studentPortal: { ...prev.studentPortal, localQuotes: nextQuotes }
                          }));
                        }}
                        placeholder="Author"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          const nextQuotes = [...(draft.studentPortal?.localQuotes || [])];
                          nextQuotes.splice(index, 1);
                          setDraft((prev) => ({
                            ...prev,
                            studentPortal: { ...prev.studentPortal, localQuotes: nextQuotes }
                          }));
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      const nextQuotes = [
                        ...(draft.studentPortal?.localQuotes || []),
                        { id: `quote-${Date.now()}`, text: '', author: '' }
                      ];
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, localQuotes: nextQuotes }
                      }));
                    }}
                  >
                    Add quote
                  </Button>
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label">Custom HTML block</label>
                  <label className="sp-inline-toggle mt-2">
                    <input
                      type="checkbox"
                      checked={Boolean(draft.studentPortal?.customHtmlEnabled)}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, customHtmlEnabled: event.target.checked }
                        }))
                      }
                    />
                    Show custom HTML content on the student dashboard
                  </label>
                  <textarea
                    className="sp-textarea mt-3 w-full"
                    rows={5}
                    value={draft.studentPortal?.customHtml || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, customHtml: event.target.value }
                      }))
                    }
                    placeholder="<div>Welcome students...</div>"
                  />
                  <div className="text-xs sp-muted mt-1">HTML is rendered as provided. Keep it simple and safe.</div>
                </div>
              </div>
              </Card>
            )}

            {studentPortalTab === 'points' && (
              <Card className="p-6 space-y-4">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Points & rewards</h3>
                <p className="sp-muted">Define how students earn points and which rewards unlock.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="sp-label">Enable points</label>
                  <label className="sp-inline-toggle mt-2">
                    <input
                      type="checkbox"
                      checked={Boolean(draft.studentPortal?.pointsEnabled)}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, pointsEnabled: event.target.checked }
                        }))
                      }
                    />
                    Show points, rewards, and leaderboard in the student portal
                  </label>
                </div>
                <div>
                  <label className="sp-label">Leaderboard scope</label>
                  <select
                    className="sp-select mt-2 w-full"
                    value={draft.studentPortal?.leaderboardScope || 'class'}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, leaderboardScope: event.target.value }
                      }))
                    }
                  >
                    <option value="class">Class/programme only</option>
                    <option value="overall">Overall</option>
                  </select>
                  <label className="sp-inline-toggle mt-2">
                    <input
                      type="checkbox"
                      checked={Boolean(draft.studentPortal?.leaderboardEnabled)}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, leaderboardEnabled: event.target.checked }
                        }))
                      }
                    />
                    Enable leaderboard (high scores)
                  </label>
                  <label className="sp-inline-toggle mt-2">
                    <input
                      type="checkbox"
                      checked={Boolean(draft.studentPortal?.leaderboardBlurNames)}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, leaderboardBlurNames: event.target.checked }
                        }))
                      }
                    />
                    Blur student names in leaderboard
                  </label>
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="sp-label">Journal entry points</label>
                  <input
                    type="number"
                    min="0"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.pointsRules?.journalEntry ?? 5}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: {
                          ...prev.studentPortal,
                          pointsRules: {
                            ...prev.studentPortal?.pointsRules,
                            journalEntry: Number(event.target.value) || 0
                          }
                        }
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="sp-label">Wellbeing check-in points</label>
                  <input
                    type="number"
                    min="0"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.pointsRules?.wellbeingCheckin ?? 8}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: {
                          ...prev.studentPortal,
                          pointsRules: {
                            ...prev.studentPortal?.pointsRules,
                            wellbeingCheckin: Number(event.target.value) || 0
                          }
                        }
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="sp-label">Assignment submission points</label>
                  <input
                    type="number"
                    min="0"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.pointsRules?.assignmentSubmission ?? 10}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: {
                          ...prev.studentPortal,
                          pointsRules: {
                            ...prev.studentPortal?.pointsRules,
                            assignmentSubmission: Number(event.target.value) || 0
                          }
                        }
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="sp-label">Default staff award points</label>
                  <input
                    type="number"
                    min="0"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.pointsRules?.teacherAwardDefault ?? 5}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: {
                          ...prev.studentPortal,
                          pointsRules: {
                            ...prev.studentPortal?.pointsRules,
                            teacherAwardDefault: Number(event.target.value) || 0
                          }
                        }
                      }))
                    }
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div className="sp-label">Reward thresholds</div>
                {(draft.studentPortal?.rewards || []).map((reward, index) => (
                  <div key={reward.id || index} className="grid md:grid-cols-[160px_1fr_auto] gap-3">
                    <input
                      type="number"
                      min="0"
                      className="sp-input w-full"
                      value={reward.points}
                      onChange={(event) => {
                        const nextRewards = [...(draft.studentPortal?.rewards || [])];
                        nextRewards[index] = {
                          ...reward,
                          points: Number(event.target.value) || 0
                        };
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, rewards: nextRewards }
                        }));
                      }}
                      placeholder="Points"
                    />
                    <input
                      className="sp-input w-full"
                      value={reward.reward}
                      onChange={(event) => {
                        const nextRewards = [...(draft.studentPortal?.rewards || [])];
                        nextRewards[index] = {
                          ...reward,
                          reward: event.target.value
                        };
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, rewards: nextRewards }
                        }));
                      }}
                      placeholder="Reward name"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      onClick={() => {
                        const nextRewards = [...(draft.studentPortal?.rewards || [])];
                        nextRewards.splice(index, 1);
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, rewards: nextRewards }
                        }));
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    const nextRewards = [
                      ...(draft.studentPortal?.rewards || []),
                      { id: `reward-${Date.now()}`, points: 0, reward: '' }
                    ];
                    setDraft((prev) => ({
                      ...prev,
                      studentPortal: { ...prev.studentPortal, rewards: nextRewards }
                    }));
                  }}
                >
                  Add reward
                </Button>
              </div>
              </Card>
            )}

            {studentPortalTab === 'extras' && (
              <Card className="p-6 space-y-4">
                <div>
                  <h3 className="sp-value" style={{ fontWeight: 600 }}>Extras</h3>
                  <p className="sp-muted">Wellbeing check-ins and how points apply to students.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="sp-label">Enable wellbeing check-ins</label>
                      <label className="sp-inline-toggle mt-2">
                        <input
                          type="checkbox"
                          checked={Boolean(draft.studentPortal?.wellbeing?.enabled)}
                          onChange={(event) =>
                            setDraft((prev) => ({
                              ...prev,
                              studentPortal: {
                                ...prev.studentPortal,
                                wellbeing: {
                                  ...(prev.studentPortal?.wellbeing || {}),
                                  enabled: event.target.checked
                                }
                              }
                            }))
                          }
                        />
                        Show wellbeing check-ins in the student portal
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="secondary" type="button" onClick={() => setWellbeingModalOpen(true)}>
                        Check-in options
                      </Button>
                      <span className="text-xs sp-muted">
                        {draft.studentPortal?.wellbeing?.frequencyDays || 7}-day frequency
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="sp-label">Points apply to</label>
                      <select
                        className="sp-select mt-2 w-full"
                        value={draft.studentPortal?.pointsScope || 'all'}
                        onChange={(event) =>
                          setDraft((prev) => ({
                            ...prev,
                            studentPortal: { ...prev.studentPortal, pointsScope: event.target.value }
                          }))
                        }
                      >
                        <option value="all">All students</option>
                        <option value="programmes">Specific programmes</option>
                        <option value="activities">Specific activities</option>
                      </select>
                      <div className="text-xs sp-muted mt-2">
                        Target points and rewards to specific cohorts if needed.
                      </div>
                    </div>
                  </div>
                </div>

                {draft.studentPortal?.pointsScope === 'programmes' && (
                  <div>
                    <label className="sp-label">Programmes eligible for points</label>
                    <SearchableChecklist
                      items={programmes}
                      selectedIds={draft.studentPortal?.pointsProgrammeIds || []}
                      onToggle={(id) => {
                        const current = new Set(draft.studentPortal?.pointsProgrammeIds || []);
                        if (current.has(id)) {
                          current.delete(id);
                        } else {
                          current.add(id);
                        }
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, pointsProgrammeIds: Array.from(current) }
                        }));
                      }}
                      getId={(programme) => programme.id}
                      getLabel={(programme) => programme.name}
                      getMeta={(programme) => programme.day ? `${programme.day} · ${programme.time || 'TBD'}` : ''}
                      placeholder="Search programmes"
                      emptyLabel="No programmes available"
                    />
                  </div>
                )}

                {draft.studentPortal?.pointsScope === 'activities' && (
                  <div>
                    <label className="sp-label">Activities eligible for points</label>
                    <SearchableChecklist
                      items={activities}
                      selectedIds={draft.studentPortal?.pointsActivityIds || []}
                      onToggle={(id) => {
                        const current = new Set(draft.studentPortal?.pointsActivityIds || []);
                        if (current.has(id)) {
                          current.delete(id);
                        } else {
                          current.add(id);
                        }
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, pointsActivityIds: Array.from(current) }
                        }));
                      }}
                      getId={(activity) => activity.id}
                      getLabel={(activity) => activity.name}
                      getMeta={(activity) => activity.date || activity.time || ''}
                      placeholder="Search activities"
                      emptyLabel="No activities available"
                    />
                  </div>
                )}
                <div className="text-xs sp-muted">
                  Suggested reward ideas: certificates, extra activity access, leadership badges, or social time credits.
                </div>
              </Card>
            )}

            {studentPortalTab === 'banner' && (
              <Card className="p-6 space-y-4">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Scrolling banner</h3>
                <p className="sp-muted">Shown across all student portal pages and logins.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="sp-label">Enable banner</label>
                  <label className="sp-inline-toggle mt-2">
                    <input
                      type="checkbox"
                      checked={Boolean(draft.studentPortal?.bannerEnabled)}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: { ...prev.studentPortal, bannerEnabled: event.target.checked }
                        }))
                      }
                    />
                    Display banner on student portal
                  </label>
                </div>
                <div>
                  <label className="sp-label">Scroll speed (seconds)</label>
                  <input
                    type="number"
                    min="6"
                    max="60"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.bannerSpeed || 18}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, bannerSpeed: Number(event.target.value) || 18 }
                      }))
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label">Banner text</label>
                  <input
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.bannerText || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, bannerText: event.target.value }
                      }))
                    }
                    placeholder="Welcome to your portal — remember to check your assignments."
                  />
                </div>
                <div>
                  <label className="sp-label">Background color</label>
                  <input
                    type="color"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.bannerBackground || '#111827'}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, bannerBackground: event.target.value }
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="sp-label">Text color</label>
                  <input
                    type="color"
                    className="sp-input mt-2 w-full"
                    value={draft.studentPortal?.bannerTextColor || '#ffffff'}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: { ...prev.studentPortal, bannerTextColor: event.target.value }
                      }))
                    }
                  />
                </div>
              </div>
              </Card>
            )}

            <Modal isOpen={wellbeingModalOpen} onClose={() => setWellbeingModalOpen(false)}>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="sp-value" style={{ fontWeight: 600 }}>Wellbeing check-in options</h3>
                    <p className="text-sm sp-muted">Configure how often students are prompted to check in.</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setWellbeingModalOpen(false)}>
                    Close
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="sp-label">Frequency (days)</label>
                    <select
                      className="sp-select mt-2 w-full"
                      value={draft.studentPortal?.wellbeing?.frequencyDays || 7}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: {
                            ...prev.studentPortal,
                            wellbeing: {
                              ...(prev.studentPortal?.wellbeing || {}),
                              frequencyDays: Number(event.target.value) || 7
                            }
                          }
                        }))
                      }
                    >
                      <option value="3">Every 3 days</option>
                      <option value="7">Weekly</option>
                      <option value="14">Every 2 weeks</option>
                      <option value="30">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="sp-label">Prompt title</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={draft.studentPortal?.wellbeing?.promptTitle || 'Wellbeing check-in'}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          studentPortal: {
                            ...prev.studentPortal,
                            wellbeing: {
                              ...(prev.studentPortal?.wellbeing || {}),
                              promptTitle: event.target.value
                            }
                          }
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="sp-label">Prompt message</label>
                  <textarea
                    className="sp-textarea mt-2 w-full"
                    rows={3}
                    value={draft.studentPortal?.wellbeing?.promptText || ''}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        studentPortal: {
                          ...prev.studentPortal,
                          wellbeing: {
                            ...(prev.studentPortal?.wellbeing || {}),
                            promptText: event.target.value
                          }
                        }
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setWellbeingModalOpen(false)}>
                    Done
                  </Button>
                </div>
              </div>
            </Modal>

            <div className="flex items-center gap-2 flex-wrap">
              {savedMessage && <span className="text-xs sp-muted">{savedMessage}</span>}
              <Button size="sm" variant="secondary" type="button" onClick={handleReset} disabled={!isDirty}>
                Reset
              </Button>
              <Button size="sm" type="button" onClick={handleSave} disabled={!isDirty}>
                Save student portal settings
              </Button>
            </div>
          </div>
          )}
        </section>
      )}

      {activeTab === 'roles' && (
        <section id="settings-panel-roles" role="tabpanel" aria-labelledby="settings-tab-roles">
          {!isAdmin ? (
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Roles & access</h3>
            <p className="sp-muted">Role management is restricted to admin users.</p>
          </Card>
        ) : (
          <Card className="p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Roles & access</h3>
                <p className="sp-muted">Control which modules each role can access.</p>
              </div>
              {roleMessage && <span className="text-xs sp-muted">{roleMessage}</span>}
            </div>
            <div className="grid lg:grid-cols-[240px_1fr] gap-5">
              <div className="space-y-3">
                <div className="sp-label">Roles</div>
                <div className="space-y-2">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      className={`sp-panel w-full text-left p-3 ${activeRoleId === role.id ? 'sp-panel--critical' : 'sp-panel--muted'}`}
                      onClick={() => setActiveRoleId(role.id)}
                    >
                      <div className="sp-value" style={{ fontWeight: 600 }}>{role.name}</div>
                      <div className="text-xs sp-muted">{role.description || 'No description'}</div>
                      <div className="text-xs sp-muted mt-1">
                        {roleAssignments[role.id] || 0} staff assigned
                      </div>
                    </button>
                  ))}
                </div>
                <div className="sp-panel sp-panel--muted p-3 space-y-2">
                  <div className="sp-label">Create new role</div>
                  <input
                    className="sp-input w-full"
                    placeholder="Role name"
                    value={newRoleName}
                    onChange={(event) => setNewRoleName(event.target.value)}
                  />
                  <input
                    className="sp-input w-full"
                    placeholder="Description (optional)"
                    value={newRoleDescription}
                    onChange={(event) => setNewRoleDescription(event.target.value)}
                  />
                  <Button size="sm" type="button" onClick={handleCreateRole}>
                    Add role
                  </Button>
                </div>
              </div>
              <div className="space-y-4">
                {activeRole ? (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="sp-label">Role name</label>
                        <input
                          className="sp-input mt-2 w-full"
                          value={activeRole.name}
                          onChange={(event) => handleRoleUpdate({ name: event.target.value })}
                        />
                      </div>
                      <div>
                        <label className="sp-label">Description</label>
                        <input
                          className="sp-input mt-2 w-full"
                          value={activeRole.description || ''}
                          onChange={(event) => handleRoleUpdate({ description: event.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="sp-label">Module access</div>
                      <div className="grid md:grid-cols-2 gap-2 mt-2">
                        {roleModules.map((module) => (
                          <label key={module.id} className="flex items-center gap-2 text-sm sp-muted">
                            <input
                              type="checkbox"
                              checked={Boolean(activeRole.pages?.[module.id])}
                              onChange={() =>
                                handleRoleUpdate({
                                  pages: {
                                    ...activeRole.pages,
                                    [module.id]: !activeRole.pages?.[module.id]
                                  }
                                })
                              }
                            />
                            <span>{module.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" type="button" onClick={handleDeleteRole} disabled={activeRole.system}>
                        Delete role
                      </Button>
                      {activeRole.system && (
                        <span className="text-xs sp-muted">System roles cannot be deleted.</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="sp-panel sp-panel--muted p-3 text-sm">
                    Select a role to view access controls.
                  </div>
                )}
              </div>
            </div>
          </Card>
          )}
        </section>
      )}

      {activeTab === 'database' && (
        <section id="settings-panel-database" role="tabpanel" aria-labelledby="settings-tab-database">
          {isAdmin ? (
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="sp-value" style={{ fontWeight: 600 }}>MySQL database settings</h3>
              <p className="sp-muted">Stored locally for now. Connect this to backend config during full integration.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sp-label">Enable database config</label>
                <label className="sp-inline-toggle mt-2">
                  <input
                    type="checkbox"
                    checked={dbDraft.enabled}
                    onChange={(event) => setDbDraft((prev) => ({ ...prev, enabled: event.target.checked }))}
                  />
                  Use MySQL connection settings
                </label>
              </div>
              <div>
                <label className="sp-label">Host</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={dbDraft.host}
                  onChange={(event) => setDbDraft((prev) => ({ ...prev, host: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Port</label>
                <input
                  type="number"
                  className="sp-input mt-2 w-full"
                  value={dbDraft.port}
                  onChange={(event) => setDbDraft((prev) => ({ ...prev, port: Number(event.target.value) || 0 }))}
                />
              </div>
              <div>
                <label className="sp-label">Database name</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={dbDraft.database}
                  onChange={(event) => setDbDraft((prev) => ({ ...prev, database: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Username</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={dbDraft.username}
                  onChange={(event) => setDbDraft((prev) => ({ ...prev, username: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Password</label>
                <input
                  className="sp-input mt-2 w-full"
                  type="password"
                  value={dbDraft.password_encrypted}
                  onChange={(event) => setDbDraft((prev) => ({ ...prev, password_encrypted: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">SSL</label>
                <label className="sp-inline-toggle mt-2">
                  <input
                    type="checkbox"
                    checked={dbDraft.ssl}
                    onChange={(event) => setDbDraft((prev) => ({ ...prev, ssl: event.target.checked }))}
                  />
                  Require SSL
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {dbMessage && <span className="text-xs sp-muted">{dbMessage}</span>}
              <Button size="sm" type="button" onClick={handleDbSave}>
                Save database settings
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>MySQL database settings</h3>
            <p className="sp-muted">Database configuration is restricted to admin users.</p>
          </Card>
          )}
        </section>
      )}

      {activeTab === 'smtp' && (
        <section id="settings-panel-smtp" role="tabpanel" aria-labelledby="settings-tab-smtp">
          {!isAdmin ? (
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Email settings</h3>
            <p className="sp-muted">Email configuration and templates are restricted to admin users.</p>
          </Card>
        ) : (
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Email (SMTP)</h3>
              <p className="sp-muted">Configure outbound email settings for reminders and logins.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sp-label">Enable SMTP</label>
                <label className="sp-inline-toggle mt-2">
                  <input
                    type="checkbox"
                    checked={smtpDraft.enabled}
                    onChange={(event) => setSmtpDraft((prev) => ({ ...prev, enabled: event.target.checked }))}
                  />
                  Use SMTP for outbound email
                </label>
              </div>
              <div>
                <label className="sp-label">Encryption</label>
                <select
                  className="sp-select mt-2 w-full"
                  value={smtpDraft.encryption}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, encryption: event.target.value }))}
                >
                  <option value="none">None</option>
                  <option value="tls">TLS</option>
                  <option value="ssl">SSL</option>
                </select>
              </div>
              <div>
                <label className="sp-label">SMTP host</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={smtpDraft.host}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, host: event.target.value }))}
                  placeholder="smtp.mailhost.com"
                />
              </div>
              <div>
                <label className="sp-label">SMTP port</label>
                <input
                  type="number"
                  className="sp-input mt-2 w-full"
                  value={smtpDraft.port}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, port: Number(event.target.value) || 0 }))}
                />
              </div>
              <div>
                <label className="sp-label">Username</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={smtpDraft.username}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, username: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Password</label>
                <input
                  className="sp-input mt-2 w-full"
                  type="password"
                  value={smtpDraft.password_encrypted}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, password_encrypted: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">From name</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={smtpDraft.from_name}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, from_name: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">From email</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={smtpDraft.from_email}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, from_email: event.target.value }))}
                  placeholder="no-reply@centre.org"
                />
              </div>
              <div>
                <label className="sp-label">Reply-to</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={smtpDraft.reply_to}
                  onChange={(event) => setSmtpDraft((prev) => ({ ...prev, reply_to: event.target.value }))}
                />
              </div>
              <div>
                <label className="sp-label">Test email address</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={smtpTestEmail}
                  onChange={(event) => setSmtpTestEmail(event.target.value)}
                  type="email"
                  placeholder="admin@centre.org"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {smtpMessage && <span className="text-xs sp-muted">{smtpMessage}</span>}
              <Button size="sm" variant="secondary" type="button" onClick={handleSmtpTest}>
                Test SMTP
              </Button>
              <Button size="sm" type="button" onClick={handleSmtpSave}>
                Save SMTP settings
              </Button>
            </div>
          </Card>
          )}
        </section>
      )}

      {activeTab === 'templates' && (
        <section id="settings-panel-templates" role="tabpanel" aria-labelledby="settings-tab-templates">
          {!isAdmin ? (
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Email templates</h3>
            <p className="sp-muted">Email templates are restricted to admin users.</p>
          </Card>
        ) : (
          <EmailTemplateManager currentUser={user} />
          )}
        </section>
      )}

      {activeTab === 'licensing' && (
        <section id="settings-panel-licensing" role="tabpanel" aria-labelledby="settings-tab-licensing">
          {isAdmin ? (
          <div className="space-y-4">
            <Card className="p-6 space-y-3">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>License status</h3>
                <p className="sp-muted">Licensing will be validated when backend integration is enabled.</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={serialKey ? 'success' : 'inactive'}>
                  {serialKey ? 'License applied' : 'No license applied'}
                </Badge>
                {licenseDraft.updated_at && (
                  <div className="text-xs sp-muted">Last updated {formatDateTime(licenseDraft.updated_at)}</div>
                )}
              </div>
            </Card>

            <Card className="p-6 space-y-3">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Serial keys</h3>
                <p className="sp-muted">Add a license/serial key to unlock packs (validation stubbed for now).</p>
              </div>
              <div>
                <label className="sp-label" htmlFor="licensing-serial-key">Serial key</label>
                <input
                  id="licensing-serial-key"
                  className="sp-input mt-2 w-full"
                  value={serialKey}
                  onChange={(event) => setSerialKey(event.target.value)}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="sr-only" aria-live="polite" role="status">
                  {[licenseMessage, packStatus].filter(Boolean).join(' ')}
                </span>
                {licenseMessage && <span className="text-xs sp-muted" role="status">{licenseMessage}</span>}
                {packStatus && <span className="text-xs sp-muted" role="status">{packStatus}</span>}
                <Button size="sm" variant="secondary" type="button" onClick={handleSerialValidate}>
                  Validate key
                </Button>
                <Button size="sm" type="button" onClick={handleLicenseSave}>
                  Save key
                </Button>
              </div>
            </Card>

            <Card className="p-6 space-y-3">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Module packs</h3>
                <p className="sp-muted">Enable or disable packs for this workspace.</p>
                <p className="text-xs sp-muted mt-1">
                  {hasPackChanges ? `${packChangeCount} unsaved change${packChangeCount > 1 ? 's' : ''}.` : 'No unsaved changes.'}
                </p>
              </div>
              <div className="space-y-3" role="group" aria-label="Module pack toggles">
                {PACK_DEFINITIONS.map((pack) => {
                  const isEnabled = Boolean(packDraft.packs?.[pack.id]);
                  const dependency = pack.dependsOn?.[0];
                  const dependencyEnabled = dependency ? Boolean(packDraft.packs?.[dependency]) : true;
                  const isLocked = pack.locked || pack.id === 'core';
                  const isDisabled = !dependencyEnabled;
                  const descriptionId = `pack-${pack.id}-description`;
                  const dependencyId = `pack-${pack.id}-dependency`;
                  return (
                    <div key={pack.id} className="sp-panel sp-panel--muted p-3 flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{pack.name}</div>
                        <div id={descriptionId} className="text-xs sp-muted">{pack.description}</div>
                        {isDisabled && dependency && (
                          <div id={dependencyId} className="text-xs sp-warning-text mt-1">Requires {dependency.replace('_', ' ')}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={isEnabled ? 'success' : 'inactive'}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <label className="sp-inline-toggle">
                          <input
                            type="checkbox"
                            role="switch"
                            aria-checked={isEnabled}
                            aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${pack.name}`}
                            aria-describedby={isDisabled && dependency ? `${descriptionId} ${dependencyId}` : descriptionId}
                            checked={isEnabled}
                            disabled={isLocked || isDisabled}
                            onChange={(event) => handlePackToggle(pack.id, event.target.checked)}
                          />
                          {isLocked ? 'Locked' : isEnabled ? 'On' : 'Off'}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {packMessage && <span className="text-xs sp-muted" role="status">{packMessage}</span>}
                <Button
                  size="sm"
                  type="button"
                  onClick={handlePackSave}
                  disabled={!hasPackChanges}
                  title={!hasPackChanges ? 'No changes to save.' : ''}
                >
                  Save pack settings
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          <Card className="p-6">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Licensing & module packs</h3>
            <p className="sp-muted">Licensing controls are restricted to admin users.</p>
          </Card>
          )}
        </section>
      )}
    </div>
  );
}
